/**
 * settings/cards/DisplayPreferencesCard.tsx
 * -----------------------------------------
 * What: Controls for theme selection and UI preferences.
 * Why: Allows users to tailor appearance and readability.
 */
import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeType } from '../../../../lib/ThemeContext';
import { useSubscription, type Currency, type DateFormat } from '../../../../lib/SubscriptionContext';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function DisplayPreferencesCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();
  const { currency, dateFormat, updateDisplayPreferences, updateThemePreference } = useSubscription();

  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showDateFormatModal, setShowDateFormatModal] = useState(false);

  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? Platform.select({
          web: { boxShadow: `0 0 6px 1px ${theme.accent}` } as any,
          default: {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          },
        })
      : {}
  ), [themeType, theme.accent]);

  const cardStyle = [styles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  return (
    <View style={cardStyle}> 
      <TouchableOpacity style={[styles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[styles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Display Preferences</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>
      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />

          {/* Theme Picker */}
          <View style={styles.preferenceSection}>
            <Text style={[styles.preferenceLabel, { color: theme.text }]}>Theme</Text>
            <TouchableOpacity
              style={[styles.selector, { backgroundColor: themeType === 'automatic' ? theme.selected : theme.inputBackground, borderColor: theme.border }]}
              onPress={() => setShowThemeModal(true)}
            >
              <Text style={[styles.selectorText, { color: theme.text }]}>{
                themeType === 'automatic'
                  ? 'Automatic (System)'
                  : themeType === 'light'
                  ? 'Light Theme'
                  : themeType === 'dark'
                  ? 'Dark Theme'
                  : 'Blue Neon'
              }</Text>
            </TouchableOpacity>
            <Modal visible={showThemeModal} transparent animationType="fade" onRequestClose={() => setShowThemeModal(false)}>
              <View style={[styles.modalOverlay, { backgroundColor: theme.modalOverlay }]}>
                <View style={[styles.modalContent, { backgroundColor: theme.modalBackground }]}> 
                  <Text style={[styles.modalTitle, { color: theme.text }]}>Select Theme</Text>
                  {[
                    { key: 'automatic', label: 'Automatic (System)' },
                    { key: 'light', label: 'Light Theme' },
                    { key: 'dark', label: 'Dark Theme' },
                    { key: 'blueNeon', label: 'Blue Neon' },
                  ].map(opt => (
                    <TouchableOpacity
                      key={opt.key}
                      style={[styles.modalOption, { backgroundColor: themeType === opt.key ? theme.selected : theme.inputBackground, borderColor: theme.border }]}
                      onPress={async () => {
                        await updateThemePreference(opt.key as ThemeType);
                        setShowThemeModal(false);
                      }}
                    >
                      <Text style={[{ color: theme.text }, themeType === opt.key && { color: theme.buttonText, fontWeight: 'bold' }]}>{opt.label}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={[styles.modalCancel, { backgroundColor: theme.inputBackground }]} onPress={() => setShowThemeModal(false)}>
                    <Text style={[styles.modalCancelText, { color: theme.text }]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
          </View>

          {/* Currency Picker */}
          <View style={styles.preferenceSection}>
            <Text style={[styles.preferenceLabel, { color: theme.text }]}>Currency</Text>
            <TouchableOpacity
              style={[styles.selector, { backgroundColor: theme.inputBackground, borderColor: theme.border }]}
              onPress={() => setShowCurrencyModal(true)}
            >
              <Text style={[styles.selectorText, { color: theme.text }]}>{currency === 'GBP' ? '£ GBP' : currency === 'USD' ? '$ USD' : '€ EUR'}</Text>
            </TouchableOpacity>
            <Modal visible={showCurrencyModal} transparent animationType="fade" onRequestClose={() => setShowCurrencyModal(false)}>
              <View style={[styles.modalOverlay, { backgroundColor: theme.modalOverlay }]}>
                <View style={[styles.modalContent, { backgroundColor: theme.modalBackground }]}> 
                  <Text style={[styles.modalTitle, { color: theme.text }]}>Select Currency</Text>
                  {['GBP', 'USD', 'EUR'].map(cur => (
                    <TouchableOpacity
                      key={cur}
                      style={[styles.modalOption, { backgroundColor: currency === cur ? theme.selected : theme.inputBackground, borderColor: theme.border }]}
                      onPress={async () => {
                        setShowCurrencyModal(false);
                        if (cur !== currency) {
                          await updateDisplayPreferences({ currency: cur as Currency });
                        }
                      }}
                    >
                      <Text style={[{ color: theme.text }, currency === cur && { color: theme.buttonText, fontWeight: 'bold' }]}>{
                        { GBP: '£ GBP', USD: '$ USD', EUR: '€ EUR' }[cur]
                      }</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={[styles.modalCancel, { backgroundColor: theme.inputBackground }]} onPress={() => setShowCurrencyModal(false)}>
                    <Text style={[styles.modalCancelText, { color: theme.text }]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
          </View>

          {/* Date Format Picker */}
          <View style={styles.preferenceSection}>
            <Text style={[styles.preferenceLabel, { color: theme.text }]}>Date Format</Text>
            <TouchableOpacity
              style={[styles.selector, { backgroundColor: theme.inputBackground, borderColor: theme.border }]}
              onPress={() => setShowDateFormatModal(true)}
            >
              <Text style={[styles.selectorText, { color: theme.text }]}>{dateFormat}</Text>
            </TouchableOpacity>
            <Modal visible={showDateFormatModal} transparent animationType="fade" onRequestClose={() => setShowDateFormatModal(false)}>
              <View style={[styles.modalOverlay, { backgroundColor: theme.modalOverlay }]}>
                <View style={[styles.modalContent, { backgroundColor: theme.modalBackground }]}> 
                  <Text style={[styles.modalTitle, { color: theme.text }]}>Select Date Format</Text>
                  {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].map(fmt => (
                    <TouchableOpacity
                      key={fmt}
                      style={[styles.modalOption, { backgroundColor: dateFormat === fmt ? theme.selected : theme.inputBackground, borderColor: theme.border }]}
                      onPress={async () => {
                        setShowDateFormatModal(false);
                        if (fmt !== dateFormat) {
                          await updateDisplayPreferences({ date_format: fmt as DateFormat });
                        }
                      }}
                    >
                      <Text style={[{ color: theme.text }, dateFormat === fmt && { color: theme.buttonText, fontWeight: 'bold' }]}>{fmt}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={[styles.modalCancel, { backgroundColor: theme.inputBackground }]} onPress={() => setShowDateFormatModal(false)}>
                    <Text style={[styles.modalCancelText, { color: theme.text }]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  cardBase: { padding: 16, borderRadius: 8, borderWidth: 1 },
  accordionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  detailsTitle: { fontSize: 17, fontWeight: '500', fontFamily: 'Inter_500Medium', marginBottom: 16 },
  preferenceSection: { marginBottom: 20 },
  preferenceLabel: { fontSize: 16, fontWeight: '600', marginBottom: 12 },
  selector: { borderRadius: 8, borderWidth: 1, padding: 14, marginBottom: 16 },
  selectorText: { fontSize: 16 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { padding: 24, borderRadius: 12, width: '80%', maxWidth: 400, alignItems: 'center' },
  modalTitle: { fontSize: 17, fontWeight: '500', fontFamily: 'Inter_500Medium', marginBottom: 12 },
  modalOption: { paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8, marginBottom: 8, width: '100%', alignItems: 'center' },
  modalCancel: { marginTop: 8, paddingVertical: 10, paddingHorizontal: 24, borderRadius: 8, width: '100%', alignItems: 'center' },
  modalCancelText: { fontSize: 16 },
}); 